import React, {
  Fragment,
  useCallback,
  useState,
  useEffect
} from 'react';
import { withStyles } from 'tss-react/mui';

import MuiAutocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Autocomplete from '@common/components/autocomplete/Autocomplete';
import WaveSelectField from '@common/components/form/WaveSelectField';
import ApiService from '@common/services/ApiService';

const styles = (theme) => ({
  clearFilters: {
    color: theme.typography.color.primary,
    '&:hover': {
      color: theme.typography.color.primary,
      cursor: 'pointer'
    },
    '&:visited': {
      color: theme.typography.color.primary
    },
    fontSize: theme.typography.pxToRem(13),
    marginRight: theme.typography.pxToRem(20),
    opacity: 0.7
  },
  filterHeader: {
    padding: `${theme.typography.pxToRem(19)} ${theme.typography.pxToRem(25)} 0 ${theme.typography.pxToRem(25)}`
  },
  filterBody: {
    paddingBottom: `${theme.typography.pxToRem(40)}`
  },
});

const ALL_OPTION = { id: 'all', label: 'All' };

const toOptionsArray = (optionsObj) => [
  ALL_OPTION,
  ...Object.entries(optionsObj).map(([key, opt]) => ({
    id: opt.value || key,
    label: opt.label || opt.name || key
  }))
];

const currentYear = new Date().getFullYear();

const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i).reduce(
  (acc, y) => ({ ...acc, [y]: { id: y.toString(), label: y.toString() } }),
  {}
);

const currencyStatusOptions = {
  current:  { label: 'Current' },
  outdated: { label: 'Outdated' },
  missing:  { label: 'Missing' }
};

const initialFilters = {
  division_id_eq: 'all',
  department_id_eq: 'all',
  course_id_eq: 'all',
  unit_id_eq: 'all',
  year_eq: currentYear.toString(),
  currency_status_eq: 'all'
};

const VcmUnitResponsesFilters = ({ classes, handleApplyFilters }) => {
  const api = new ApiService();

  const [filters, setFilters] = useState(initialFilters);
  const [educatorLabel, setEducatorLabel] = useState('');
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);

  const { division_id_eq, department_id_eq, course_id_eq, unit_id_eq, year_eq, currency_status_eq } = filters;

  useEffect(() => {
    Promise.all([
      api.query('/api/v1/divisions/options'),
      api.query('/api/v1/departments/options')
    ]).then(([divisions, departments]) => {
      setDivisionOptions(divisions);
      setDepartmentOptions(departments);
    });
  }, []);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, department_id_eq: 'all' }));
  }, [division_id_eq]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, course_id_eq: 'all', unit_id_eq: 'all' }));
    setUnitOptions([]);
    api.query(`/api/v1/courses/options?year=${year_eq}`).then(setCourseOptions);
  }, [year_eq]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, unit_id_eq: 'all' }));
    setUnitOptions([]);

    const courseId = parseInt(course_id_eq);
    const yearParam = `year=${year_eq}`;
    if (courseId) {
      api.query(`/api/v1/units/options?${yearParam}&course_id=${courseId}`).then(setUnitOptions);
    } else {
      api.query(`/api/v1/units/options?${yearParam}`).then(setUnitOptions);
    }
  }, [course_id_eq, year_eq]);

  const departments = (() => {
    const divisionId = parseInt(division_id_eq);
    if (!divisionId) return departmentOptions;
    const result = {};
    Object.keys(departmentOptions).forEach((key) => {
      if (departmentOptions[key].division_id === divisionId) {
        result[key] = departmentOptions[key];
      }
    });
    return result;
  })();

  const handleInputChange = useCallback(
    (filterName, event) => {
      const value = event.target.value;
      if (value !== null && value !== 'all') {
        setFilters((prev) => ({ ...prev, [filterName]: value }));
      } else {
        setFilters((prev) => {
          const { [filterName]: _removed, ...rest } = prev;
          return { ...rest, [filterName]: 'all' };
        });
      }
    },
    []
  );

  const handleChangeEducator = useCallback((id, value) => {
    setEducatorLabel(value);
    if (id !== null && id !== 'all') {
      setFilters((prev) => ({ ...prev, user_id_eq: id }));
    } else {
      setFilters((prev) => {
        const { user_id_eq, ...rest } = prev;
        return rest;
      });
    }
  }, []);

  const makeFieldProps = (filterName, fieldValue) => ({
    name: filterName,
    onChange: (e) => handleInputChange(filterName, e),
    value: fieldValue
  });

  const applyFilters = useCallback(() => {
    const newFilters = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== null && filters[key] !== 'all') {
        newFilters[key] = filters[key];
      }
    });
    handleApplyFilters(newFilters);
  }, [filters, handleApplyFilters]);

  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
    handleChangeEducator(null, '');
    handleApplyFilters({ year_eq: currentYear.toString() });
  }, [handleApplyFilters]);

  const courseOptionsArray = toOptionsArray(courseOptions);
  const selectedCourse = courseOptionsArray.find((o) => o.id === course_id_eq) || ALL_OPTION;

  const unitOptionsArray = toOptionsArray(unitOptions);
  const selectedUnit = unitOptionsArray.find((o) => o.id === unit_id_eq) || ALL_OPTION;

  return (
    <Fragment>
      <Grid
        container
        className={classes.filterHeader}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h6">Filter</Typography>
        <Grid>
          <a className={classes.clearFilters} onClick={clearFilters}>
            Clear
          </a>
          <Button color="secondary" onClick={applyFilters} size="small" variant="contained">
            <Typography variant="button">Filter</Typography>
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={3} className={[classes.filterHeader, classes.filterBody]}>
        <Grid size={3}>
          <WaveSelectField
            filterByAllItem
            filterByAllItemLabel="All"
            noValidation
            label="Division"
            menuItems={divisionOptions}
            {...makeFieldProps('division_id_eq', division_id_eq)}
          />
        </Grid>
        <Grid size={3}>
          <WaveSelectField
            filterByAllItem
            filterByAllItemLabel="All"
            noValidation
            label="Department"
            menuItems={departments}
            {...makeFieldProps('department_id_eq', department_id_eq)}
          />
        </Grid>
        <Grid size={3}>
          <MuiAutocomplete
            options={courseOptionsArray}
            getOptionLabel={(option) => option.label || ''}
            value={selectedCourse}
            onChange={(_, option) =>
              handleInputChange('course_id_eq', { target: { value: option?.id || 'all' } })
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable
            slotProps={{
              paper: {
                sx: {
                  '& .MuiAutocomplete-option': { '&:before': { display: 'none' } }
                }
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Course" variant="standard" />
            )}
          />
        </Grid>
        <Grid size={3}>
          <MuiAutocomplete
            options={unitOptionsArray}
            getOptionLabel={(option) => option.label || ''}
            value={selectedUnit}
            onChange={(_, option) =>
              handleInputChange('unit_id_eq', { target: { value: option?.id || 'all' } })
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable
            slotProps={{
              paper: {
                sx: {
                  '& .MuiAutocomplete-option': { '&:before': { display: 'none' } }
                }
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Unit" variant="standard" />
            )}
          />
        </Grid>

        <Grid size={3}>
          <Autocomplete
            label="Educator"
            name="user_id"
            onChange={handleChangeEducator}
            role={['educator', 'guest']}
            value={educatorLabel || ''}
          />
        </Grid>
        <Grid size={3}>
          <WaveSelectField
            noValidation
            label="Year"
            menuItems={yearOptions}
            {...makeFieldProps('year_eq', year_eq)}
          />
        </Grid>
        <Grid size={3}>
          <WaveSelectField
            filterByAllItem
            filterByAllItemLabel="All"
            noValidation
            label="Currency Status"
            menuItems={currencyStatusOptions}
            {...makeFieldProps('currency_status_eq', currency_status_eq)}
          />
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default withStyles(VcmUnitResponsesFilters, styles, { withTheme: true });
